/**
 * SearchSuggestPanel.tsx
 * 상단 검색바 focus 시 노출되는 패널 (요구사항 #246)
 * - 최근 검색어 (localStorage, 개별/전체 삭제)
 * - 추천 검색어 (어드민이 등록한 키워드)
 * - 키워드 클릭 → onSelect(keyword) 호출 (부모가 검색 navigate)
 */

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getRecentSearches,
  removeRecentSearch,
  clearRecentSearches,
} from "../../utils/recentSearch";
import { loadSearchIndex, searchBanners, SearchItem } from "../../utils/searchEngine";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.trippang.com";

interface Props {
  onSelect: (keyword: string) => void;
  onClose: () => void;
  /** 검색바의 현재 입력값 — # 으로 시작하면 hashtag 자동완성 모드 */
  query?: string;
  /** 선택 국가 — 검색을 이 국가로 스코프 (결과페이지와 일치) */
  countryId?: number;
}

export default function SearchSuggestPanel({ onSelect, onClose, query, countryId }: Props) {
  const { t, i18n } = useTranslation();
  const [recents, setRecents] = useState<string[]>([]);
  const [suggested, setSuggested] = useState<string[]>([]);
  const [popularTags, setPopularTags] = useState<{ tag: string; count: number }[]>([]);
  // # prefix 자동완성 결과
  const [hashtagSuggest, setHashtagSuggest] = useState<string[]>([]);
  // #402 클라 증분 배너 매칭 (초성+음절, 서버요청 0)
  const [bannerMatches, setBannerMatches] = useState<SearchItem[]>([]);

  // 검색바 focus 시 인덱스 1회 prefetch (타이핑 전 미리 로드 → 첫 타이핑 즉시 반응)
  useEffect(() => {
    loadSearchIndex(i18n.language).catch(() => {});
  }, [i18n.language]);

  // 입력값 변경 시 클라이언트에서만 필터 (# 모드 제외) — 서버요청 없음
  useEffect(() => {
    const q = (query || "").trim();
    if (!q || q.startsWith("#")) {
      setBannerMatches([]);
      return;
    }
    let alive = true;
    loadSearchIndex(i18n.language).then((idx) => {
      if (alive) setBannerMatches(searchBanners(q, idx, 500, countryId)); // 전체 목록(스크롤), 안전 상한 500. 국가 스코프
    });
    return () => { alive = false; };
  }, [query, i18n.language, countryId]);

  // 최근 검색어 초기 + 변경 이벤트 구독
  useEffect(() => {
    const refresh = () => setRecents(getRecentSearches());
    refresh();
    window.addEventListener("recent-search-changed", refresh);
    return () => window.removeEventListener("recent-search-changed", refresh);
  }, []);

  // 추천 검색어 fetch
  useEffect(() => {
    fetch(`${API_BASE}/public/business/suggested-keywords`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d: { items: { id: number; keyword: string }[] }) =>
        setSuggested(Array.isArray(d.items) ? d.items.map((x) => x.keyword) : []),
      )
      .catch(() => setSuggested([]));
  }, []);

  // 인기 해시태그 fetch (top 10)
  useEffect(() => {
    fetch(`${API_BASE}/public/business/popular-hashtags?limit=10`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d: { tag: string; count: number }[]) =>
        setPopularTags(Array.isArray(d) ? d : []),
      )
      .catch(() => setPopularTags([]));
  }, []);

  // # prefix 자동완성 — query 가 # 으로 시작할 때만 ajax (debounce 200ms)
  useEffect(() => {
    if (!query || !query.startsWith("#")) {
      setHashtagSuggest([]);
      return;
    }
    const term = query.slice(1).trim();
    if (term.length < 1) {
      setHashtagSuggest([]);
      return;
    }
    const tm = setTimeout(() => {
      fetch(`${API_BASE}/public/business/tags?q=${encodeURIComponent(term)}`)
        .then((r) => (r.ok ? r.json() : []))
        .then((d: string[]) => setHashtagSuggest(Array.isArray(d) ? d.slice(0, 15) : []))
        .catch(() => setHashtagSuggest([]));
    }, 200);
    return () => clearTimeout(tm);
  }, [query]);

  const inHashtagMode = !!query && query.startsWith("#");

  // 셋 다 비어있어야만 placeholder
  const allEmpty =
    recents.length === 0 && suggested.length === 0 && popularTags.length === 0;

  return (
    <div
      className="absolute left-0 right-0 top-full z-[100] mt-1 rounded-lg border border-slate-300 bg-white shadow-xl"
      onMouseDown={(e) => e.preventDefault() /* blur 방지 */}
    >
      {/* 우상단 닫기 버튼 — input blur 회피용 mousedown preventDefault, 실제 닫기는 onClick */}
      <button
        type="button"
        onClick={onClose}
        aria-label={t("common.close") || "닫기"}
        className="absolute top-1.5 right-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
      >
        <X className="h-4 w-4" />
      </button>

      {/* #402 매칭 배너 — 클라 증분 검색(초성+음절, 서버요청 0). # 모드 아닐 때만 */}
      {!inHashtagMode && bannerMatches.length > 0 && (
        <div className="max-h-72 overflow-y-auto border-b border-slate-200">
          {bannerMatches.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => {
                // 클릭 → 해당 배너 상세 바로 열기 (HomePage open-banner 핸들러가 fetch+오픈)
                window.dispatchEvent(new CustomEvent("open-banner", { detail: { bannerId: b.id } }));
                onClose();
              }}
              className="flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left hover:bg-slate-50"
            >
              <div className="w-full truncate text-sm font-medium text-slate-800">{b.t || b.bn || "-"}</div>
              {b.s && <div className="w-full truncate text-xs text-slate-500">{b.s}</div>}
              {(b.bt || b.bn) && (
                <div className="flex w-full gap-2 truncate text-[11px] text-slate-400">
                  {b.bt && <span className="truncate">{b.bt}</span>}
                  {b.bn && <span className="truncate">{b.bn}</span>}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* # prefix 자동완성 — query 가 # 로 시작할 때만 노출 */}
      {inHashtagMode && hashtagSuggest.length > 0 && (
        <div className="px-3 py-3 border-b border-slate-200 bg-amber-50">
          <div className="mb-2 text-xs font-bold text-amber-700">
            # {t("search.hashtag_match_title") || "해시태그 자동완성"}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {hashtagSuggest.map((tag) => (
              <button
                key={tag}
                onClick={() => onSelect(`#${tag}`)}
                className="rounded-full border border-amber-400 bg-white px-2.5 py-1 text-xs text-amber-700 hover:bg-amber-100"
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>
      )}
      {inHashtagMode && hashtagSuggest.length === 0 && query!.length > 1 && (
        <div className="px-3 py-2 border-b border-slate-200 bg-amber-50 text-[11px] text-amber-700">
          {t("search.hashtag_no_match") || "일치하는 해시태그 없음"}
        </div>
      )}

      {/* 최근 검색어 */}
      <div className="px-3 py-3 border-b border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-slate-900">{t("search.recent_title")}</span>
          {recents.length > 0 && (
            <button
              onClick={() => clearRecentSearches()}
              className="text-xs text-sky-600 hover:underline"
            >
              {t("search.clear_all")}
            </button>
          )}
        </div>
        {recents.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {recents.map((kw) => (
              <span
                key={kw}
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-slate-50 px-2 py-1 text-xs text-slate-800"
              >
                <button
                  onClick={() => onSelect(kw)}
                  className="hover:text-sky-600"
                >
                  {kw}
                </button>
                <button
                  onClick={() => removeRecentSearch(kw)}
                  className="ml-0.5 text-slate-400 hover:text-rose-500"
                  aria-label="remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <div className="text-xs text-slate-400">{t("search.recent_empty")}</div>
        )}
      </div>

      {/* 추천 검색어 */}
      <div className="px-3 py-3">
        <div className="mb-2 text-xs font-bold text-slate-900">{t("search.suggested_title")}</div>
        {suggested.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {suggested.map((kw) => (
              <button
                key={kw}
                onClick={() => onSelect(kw)}
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-sky-50 hover:border-sky-300 hover:text-sky-600"
              >
                {kw}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-xs text-slate-400">{t("search.suggested_empty")}</div>
        )}
      </div>

      {/* 인기 해시태그 (top 10) — 활성 배너의 hashtags 빈도 카운트 */}
      {popularTags.length > 0 && (
        <div className="px-3 py-3 border-t border-slate-200">
          <div className="mb-2 text-xs font-bold text-slate-900">
            🔥 {t("search.popular_tags_title") || "인기 검색어"}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {popularTags.map((p) => (
              <button
                key={p.tag}
                onClick={() => onSelect(p.tag)}
                title={`${p.count}건`}
                className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2.5 py-1 text-xs text-sky-700 hover:bg-sky-100 hover:border-sky-400"
              >
                <span className="font-semibold">{p.tag}</span>
                <span className="text-[10px] text-sky-500">{p.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 모두 빈 경우만 추가 안내 (어드민이 추천 키워드/해시태그 아직 없음) */}
      {allEmpty && (
        <div className="px-3 py-2 border-t border-slate-200 text-[11px] text-slate-400">
          {t("search.hint_type_to_search")}
        </div>
      )}
    </div>
  );
}
